/**
 * Peace with terms (Phase 13c, ADR-0071): what it takes to end a war.
 *
 * A side that wants peace — its war done, or going badly — makes it plainly. A side
 * that means to fight on can be bought off: the loser hands over gold, a technology or
 * a city, and the winner weighs the terms against what it is fighting for (the city
 * its army is marching on) exactly as it weighs any deal. The player's offers are
 * judged by the same `appraisePeace` the AI's own are, so a refusal always has a reason.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { weightsFor } from '../ai/personality.js';
import { citiesOf } from '../cities/index.js';
import { isCapital } from '../cities/economy.js';
import { knows } from '../research/knowledge.js';
import type { City, DealItem, GameState, Id } from '../state/types.js';
import { techValue, transfer, itemValid, type Appraisal, type DealRefusal } from './deals.js';
import { atWar, makePeace, peaceReason, wantsPeace } from './index.js';
import { relationOf } from './relation.js';

/** The kinds of thing that can be terms of peace. */
const TERMS = new Set<DealItem['kind']>(['gold', 'goldPerTurn', 'tech', 'city']);

/**
 * What a city is worth, in gold: `cityValuePerCitizen` for each citizen and
 * `cityBuildingShare` of what its buildings cost.
 */
export function cityValue(content: Content, city: City): number {
  const rules = content.rules.diplomacy;
  let built = 0;
  for (const id of city.buildings) built += content.buildings.get(id).cost;
  return Math.round(city.population * rules.cityValuePerCitizen + built * rules.cityBuildingShare);
}

/** Why `from` cannot offer these terms of peace to `to`, or null. */
export function peaceBlocker(state: GameState, content: Content, from: Id, to: Id, give: DealItem[], get: DealItem[]): DealRefusal | null {
  const a = state.civs[from];
  const b = state.civs[to];
  if (!a || !b || a.defeated || b.defeated || from === to) return 'NoSuchCiv';
  const seen = new Set<string>();
  for (const [list, giver, receiver] of [
    [give, a, b],
    [get, b, a],
  ] as const) {
    for (const item of list) {
      if (!TERMS.has(item.kind)) return 'BadDealItem';
      const key = `${giver.id}:${item.kind}:${item.kind === 'tech' ? item.tech : item.kind === 'city' ? item.city : ''}`;
      if (seen.has(key)) return 'BadDealItem';
      seen.add(key);
      const refused = itemValid(state, content, giver, receiver, item);
      if (refused) return refused;
    }
  }
  return null;
}

/**
 * What one term is worth to the judge, in gold: to get, or to give up. A war is fought
 * for land: gold and knowledge offered to end one count at `peaceTradeShare` of their
 * worth — a city at its full worth (ADR-0071).
 */
function worth(state: GameState, content: Content, judge: Id, item: DealItem, receiving: boolean): number {
  const turns = content.rules.diplomacy.paymentTurns;
  const ai = weightsFor(content, state.civs[judge]);
  const share = receiving ? ai.peaceTradeShare : 1;
  switch (item.kind) {
    case 'gold':
      return item.amount * share;
    case 'goldPerTurn':
      return item.amount * turns * (receiving ? 0.75 : 1) * share;
    case 'tech':
      return techValue(content, item.tech) * (receiving ? share : 0.5);
    case 'city': {
      const city = state.cities[item.city];
      return city ? cityValue(content, city) * (receiving ? 1 : ai.cityKeepFactor) : 0;
    }
    default:
      return 0;
  }
}

/** The judge's city its enemy's army is marching on, if it is marching on one. */
function threatened(state: GameState, judge: Id, enemy: Id): City | null {
  const relation = relationOf(state, judge, enemy);
  if (!relation) return null;
  const theirs = relation.a === enemy ? relation.campaignA : relation.campaignB;
  const id = theirs?.aims[0];
  const city = id === undefined ? undefined : state.cities[id];
  return city && city.owner === judge ? city : null;
}

/** The enemy cities the judge's own war is still for, the next target first. */
function prizes(state: GameState, judge: Id, enemy: Id): City[] {
  const relation = relationOf(state, judge, enemy);
  if (!relation) return [];
  const mine = relation.a === judge ? relation.campaignA : relation.campaignB;
  if (!mine) return [];
  return mine.aims.map((id) => state.cities[id]).filter((c): c is City => c !== undefined && c.owner === enemy);
}

/**
 * The least the judge must come out ahead, in gold, to make peace now. Negative when
 * it is losing: it would pay that much — up to the worth of the city the enemy is
 * marching on, or `peaceGoldShare` of its treasury. Zero when its war is done. While it
 * means to fight on, `peacePriceShare` of the city it is marching on and half that
 * share of each other city its war is still for (or `peaceBasePrice` with none) — one
 * city does not buy off a war for three.
 */
export function peacePrice(state: GameState, content: Content, judge: Id, enemy: Id): number {
  const ai = weightsFor(content, state.civs[judge]);
  if (wantsPeace(state, content, judge, enemy)) {
    const why = peaceReason(state, content, judge, enemy);
    if (why === 'aimMet' || why === 'stalemate') return 0;
    const city = threatened(state, judge, enemy);
    const treasury = state.civs[judge]?.treasury ?? 0;
    return -Math.max(city ? cityValue(content, city) * ai.cityKeepFactor : 0, treasury * ai.peaceGoldShare);
  }
  const targets = prizes(state, judge, enemy);
  if (targets.length === 0) return ai.peaceBasePrice;
  // Cities the war is for but has not yet found count as much as the next target.
  const relation = relationOf(state, judge, enemy)!;
  const mine = relation.a === judge ? relation.campaignA : relation.campaignB;
  const wanted = Math.max(targets.length, (mine?.goal ?? 1) - (mine?.taken ?? 0));
  const first = cityValue(content, targets[0]!);
  let price = 0;
  for (let i = 0; i < wanted; i++) price += (targets[i] ? cityValue(content, targets[i]!) : first) * ai.peacePriceShare * (i === 0 ? 1 : 0.5);
  return price;
}

/**
 * How `judge` sees peace with `other` on these terms: it would get `gets` and give
 * `gives`. Accepts when what it gains, less what it gives up, is at least its price.
 */
export function appraisePeace(state: GameState, content: Content, judge: Id, other: Id, gets: DealItem[], gives: DealItem[]): Appraisal {
  let gain = 0;
  let loss = 0;
  for (const item of gets) gain += worth(state, content, judge, item, true);
  for (const item of gives) loss += worth(state, content, judge, item, false);
  const price = peacePrice(state, content, judge, other);
  const net = gain - loss;
  if (net >= price) return { gain, loss, accept: true, reason: gets.length > 0 ? 'They accept your terms' : 'They accept', shortfall: 0 };
  // In gold to add: gold offered for peace counts at peaceTradeShare of its worth.
  const shortfall = Math.ceil((price - net) / Math.max(0.01, weightsFor(content, state.civs[judge]).peaceTradeShare));
  if (price <= 0) return { gain, loss, accept: false, reason: 'They will not give so much for peace', shortfall: 0 };
  return { gain, loss, accept: false, reason: `They mean to fight on — about ${shortfall} gold more might change their mind`, shortfall };
}

/**
 * The terms an AI civilisation offers when it wants peace: nothing, if the enemy would
 * take plain peace; otherwise gold, then technologies the enemy lacks, then the city
 * the enemy's army is marching on — no more than it would pay (its `peacePrice`).
 * Null when nothing it would give is enough.
 */
export function peaceTermsFor(state: GameState, content: Content, civId: Id, enemy: Id): { give: DealItem[]; get: DealItem[] } | null {
  const civ = state.civs[civId];
  const them = state.civs[enemy];
  if (!civ || !them) return null;
  if (appraisePeace(state, content, enemy, civId, [], []).accept) return { give: [], get: [] };
  const budget = -peacePrice(state, content, civId, enemy);
  if (budget <= 0) return null;
  const give: DealItem[] = [];
  let spent = 0;
  const enough = (): boolean => appraisePeace(state, content, enemy, civId, give, []).accept;

  const need = (): number => appraisePeace(state, content, enemy, civId, give, []).shortfall;
  const gold = Math.min(civ.treasury, Math.floor(budget), need());
  if (gold > 0) {
    give.push({ kind: 'gold', amount: gold });
    spent += gold;
    if (enough()) return { give, get: [] };
  }
  const techs = civ.techs
    .filter((t) => !knows(them, t) && content.technologies.get(t).prerequisites.every((p) => knows(them, p)))
    .sort((p, q) => techValue(content, q) - techValue(content, p) || p - q);
  // One technology at most: a war is not ended by emptying the library.
  for (const tech of techs) {
    const cost = techValue(content, tech) * 0.5;
    if (spent + cost > budget) continue;
    give.push({ kind: 'tech', tech });
    spent += cost;
    if (enough()) return { give, get: [] };
    break;
  }
  const city = threatened(state, civId, enemy);
  if (city && !isCapital(content, city) && citiesOf(state, civId).length > 1) {
    const cost = cityValue(content, city) * weightsFor(content, civ).cityKeepFactor;
    if (cost <= budget) {
      // A city settles it: the gold and knowledge stay home.
      const terms: DealItem[] = [{ kind: 'city', city: city.id }];
      if (appraisePeace(state, content, enemy, civId, terms, []).accept) return { give: terms, get: [] };
    }
  }
  return null;
}

/** Make peace on these terms: `from` gives `give` and receives `get`. */
export function executePeace(state: GameState, content: Content, from: Id, to: Id, give: DealItem[], get: DealItem[]): GameEvent[] {
  const events = makePeace(state, content, from, to);
  const a = state.civs[from]!;
  const b = state.civs[to]!;
  if (give.length + get.length === 0) return events;
  const moved: GameEvent[] = [];
  for (const item of give) transfer(state, content, a, b, item, moved);
  for (const item of get) transfer(state, content, b, a, item, moved);
  events.push({ t: 'PeaceTerms', from, to, give, get }, ...moved);
  return events;
}

/** Terms written out for the player: "Susa, 40 gold and Writing". */
export function describeTerms(state: GameState, content: Content, items: DealItem[]): string {
  const parts = items.map((item) => {
    switch (item.kind) {
      case 'gold':
        return `${item.amount} gold`;
      case 'goldPerTurn':
        return `${item.amount} gold a turn`;
      case 'tech':
        return content.technologies.get(item.tech).name;
      case 'city':
        return state.cities[item.city]?.name ?? 'a city';
      default:
        return '';
    }
  });
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Whether two civilisations are at war and this offer could still be carried out. */
export function termsStillValid(state: GameState, content: Content, from: Id, to: Id, give: DealItem[], get: DealItem[]): boolean {
  return atWar(state, from, to) && peaceBlocker(state, content, from, to, give, get) === null;
}

